/**
 * AutomationFlowContext - Provides shared callbacks and state for automation flow components
 *
 * This context allows deeply nested components (like SegmentNode) to access
 * callbacks without prop drilling through React Flow's node data system.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type * as Api from '@ViewModels';

interface AutomationFlowContextValue {
	/** Callback to add a new path/case to a switch node */
	onAddPath?: (switchNodeId: string) => Promise<void>;
	/** Callback to update node data */
	updateNodeData?: (nodeId: string, data: Record<string, unknown>) => void;
	/** Callback to persist segment case criteria changes to the API */
	onUpdateCaseCriteria?: (switchNodeId: string, caseId: string, filter: Api.IContactFilterCriteria | undefined) => void;
	/** Callback to persist segment case name changes to the API */
	onUpdateCaseName?: (switchNodeId: string, caseId: string, name: string) => void;
	/** Whether the Add Path button should be shown on segment nodes */
	showAddPath: boolean;
	/** Whether the canvas is in readonly mode */
	isReadOnly: boolean;
	/** Live automation template from the query (for trigger settings parity with list view) */
	automationTemplate?: Api.IAutomationTemplate;
	/** Impersonation context for admin operations */
	impersonationContext?: Api.IImpersonationContext;
}

const AutomationFlowContext = createContext<AutomationFlowContextValue | null>(null);

interface AutomationFlowProviderProps {
	children: ReactNode;
	onAddPath?: (switchNodeId: string) => Promise<void>;
	updateNodeData?: (nodeId: string, data: Record<string, unknown>) => void;
	onUpdateCaseCriteria?: (switchNodeId: string, caseId: string, filter: Api.IContactFilterCriteria | undefined) => void;
	onUpdateCaseName?: (switchNodeId: string, caseId: string, name: string) => void;
	showAddPath?: boolean;
	isReadOnly?: boolean;
	automationTemplate?: Api.IAutomationTemplate;
	impersonationContext?: Api.IImpersonationContext;
}

export function AutomationFlowProvider({
	children,
	onAddPath,
	updateNodeData,
	onUpdateCaseCriteria,
	onUpdateCaseName,
	showAddPath = false,
	isReadOnly = false,
	automationTemplate,
	impersonationContext,
}: AutomationFlowProviderProps) {
	// Memoize the context value to prevent all consumer nodes from
	// re-rendering on every parent render (the canvas re-renders frequently
	// during drag/resize).
	const value = useMemo<AutomationFlowContextValue>(
		() => ({
			onAddPath,
			updateNodeData,
			onUpdateCaseCriteria,
			onUpdateCaseName,
			showAddPath,
			isReadOnly,
			automationTemplate,
			impersonationContext,
		}),
		[
			onAddPath,
			updateNodeData,
			onUpdateCaseCriteria,
			onUpdateCaseName,
			showAddPath,
			isReadOnly,
			automationTemplate,
			impersonationContext,
		]
	);

	return <AutomationFlowContext.Provider value={value}>{children}</AutomationFlowContext.Provider>;
}

export function useAutomationFlowContext(): AutomationFlowContextValue {
	const context = useContext(AutomationFlowContext);
	if (!context) {
		// Return safe defaults if used outside provider (e.g., in Storybook)
		return {
			showAddPath: false,
			isReadOnly: true,
		};
	}
	return context;
}
